import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'What is Anime HOT or NOT?',
      answer: 'Anime HOT or NOT is a community-driven platform where anime fans can rate characters from their favorite anime series on a scale of 1 to 10. Our platform features thousands of characters from hundreds of anime series, allowing users to discover new characters, see community rankings, and engage in discussions about their favorite anime.'
    },
    {
      question: 'How do I start rating anime characters?',
      answer: 'To start rating characters, first create a free account by clicking the sign-up button. Once logged in, navigate to the Rate page where you will be presented with random anime characters. Rate each character from 1 to 10 based on your personal opinion, or skip characters you are not familiar with. You can filter characters by gender preference using the filter buttons at the top of the page.'
    },
    {
      question: 'Can I change my rating after submitting it?',
      answer: 'Yes, you can update your ratings at any time. When you encounter a character you have already rated, the system will display your previous rating and allow you to change it. Your updated rating will immediately replace your old rating and contribute to the character\'s new average score.'
    },
    {
      question: 'How are the Top 10 rankings calculated?',
      answer: 'The Top 10 rankings are calculated based on the average rating each character receives from all community members. Characters must have a minimum number of ratings to appear in the Top 10 to ensure the rankings are representative and not skewed by a small number of votes. The rankings are updated in real-time as new ratings are submitted.'
    },
    {
      question: 'What does the skip button do?',
      answer: 'The skip button allows you to move to the next character without rating the current one. This is useful when you encounter a character you are not familiar with or do not have an opinion about. Skipped characters may appear again later, giving you another opportunity to rate them if you choose.'
    },
    {
      question: 'How do I add characters to my favorites?',
      answer: 'You can add up to five characters to your favorites list from any character page. Simply click the heart icon or the "Add to Favorites" button on a character\'s page. Your favorite characters will be displayed on your profile page for other users to see. You can remove characters from your favorites at any time to make room for new ones.'
    },
    {
      question: 'How does the follow system work?',
      answer: 'You can follow other users to stay updated on their activity and ratings. Visit any user\'s profile page and click the "Follow" button to start following them. You can view your followers and the users you are following on your own profile page. Following someone allows you to see their latest ratings and activity, helping you discover new characters and connect with users who share similar tastes.'
    },
    {
      question: 'How do notifications work?',
      answer: 'The notification system keeps you informed about important activity related to your account. You will receive notifications when someone follows you, rates a character you favorited, or interacts with your content. Click the bell icon in the navigation bar to view your notifications. Unread notifications are highlighted, and you can mark them as read individually or all at once. You can also clear notifications you no longer need.'
    },
    {
      question: 'What is the Top 5 Raters section?',
      answer: 'The Top 5 Raters section showcases the most active members of our community based on the total number of character ratings they have submitted. This leaderboard recognizes users who contribute the most to building our character rating database. The rankings are updated in real-time, and you can click on any top rater\'s profile to see their favorite characters and rating activity.'
    },
    {
      question: 'Can I customize my profile avatar?',
      answer: 'Yes, you can use any character from our database as your profile avatar. Go to your profile page and click on your current avatar to open the avatar editor. Search for any character, select them, and use the zoom and positioning controls to frame the image exactly how you want it. Your custom avatar will be displayed next to your comments and on your profile.'
    },
    {
      question: 'How do I browse characters by anime series?',
      answer: 'You can browse all available anime series by clicking on "All Series" in the navigation menu. This will show you an alphabetically organized list of all anime series in our database. Click on any series to see all characters from that anime. You can also use the search function to quickly find a specific series.'
    },
    {
      question: 'What are the gender filters for?',
      answer: 'The gender filters on the Rate page allow you to customize which characters you see. You can choose to rate only female characters, only male characters, or all characters regardless of gender. This feature helps you focus on rating the types of characters you are most interested in and familiar with.'
    },
    {
      question: 'How do comments work?',
      answer: 'You can leave comments on any character page to share your thoughts and opinions about that character. Other users can reply to your comments, creating discussion threads. Comments are moderated to ensure they remain respectful and appropriate. You can edit or delete your own comments at any time.'
    },
    {
      question: 'Is my account information secure?',
      answer: 'Yes, we take security seriously. Your account information is protected with industry-standard security measures including encrypted passwords and secure authentication. We never share your personal information with third parties. You can review our privacy practices and security measures in our terms of service.'
    },
    {
      question: 'Can I suggest new characters to be added?',
      answer: 'Yes! We welcome suggestions from the community. You can submit character or anime series suggestions using the "Suggest Character/Series" form located in the footer of every page. We regularly review submissions and add new characters, especially from popular and newly released anime series.'
    },
    {
      question: 'What should I do if I see inappropriate content?',
      answer: 'If you encounter inappropriate comments, images, or any content that violates our community guidelines, please let us know by using the "Submit Feedback" form in the footer of our website. Our moderation team reviews all reports promptly and takes appropriate action to maintain a safe and respectful community environment.'
    },
    {
      question: 'How often are new characters added to the database?',
      answer: 'We regularly update our database with new characters, especially from currently airing anime series and popular recent releases. Major updates typically occur weekly, but the frequency can vary based on the anime season and community demand. Follow our social media channels to stay updated on new character additions.'
    },
    {
      question: 'Can I see my rating history?',
      answer: 'Currently, your profile page displays your total rating count and your top five favorite characters. A full rating history feature showing every character you have rated and the scores you gave is not yet available, but we are considering adding it in a future update.'
    },
    {
      question: 'Why do some characters have more ratings than others?',
      answer: 'Character rating counts vary based on several factors including the popularity of the anime series, how long the character has been in our database, and how frequently the character appears in the random rating rotation. Characters from more popular or mainstream anime series tend to accumulate ratings faster than characters from niche or lesser-known series.'
    },
    {
      question: 'What makes a character appear in the Top 10?',
      answer: 'To appear in the Top 10, a character must have both a high average rating and a sufficient number of total ratings. This ensures that the rankings reflect genuine community consensus rather than being influenced by a small number of votes. The exact minimum rating threshold is adjusted periodically to maintain ranking quality.'
    },
    {
      question: 'Can I rate the same character multiple times?',
      answer: 'No, you can only have one active rating per character. However, you can update your rating at any time if your opinion changes. When you rate a character you have already rated, your new rating replaces the old one rather than creating a duplicate rating.'
    },
    {
      question: 'Can I unfollow someone I previously followed?',
      answer: 'Yes, you can unfollow any user at any time. Simply visit their profile page and click the "Unfollow" button. You can also manage your following list from your own profile page. Unfollowing someone does not notify them, and you can follow them again later if you change your mind.'
    },
    {
      question: 'Will I be notified when someone unfollows me?',
      answer: 'No, you will not receive a notification when someone unfollows you. Notifications are only sent for positive interactions such as new followers, ratings on your favorite characters, and replies to your comments. This helps maintain a positive community atmosphere and reduces unnecessary notifications.'
    },
    {
      question: 'Is there a mobile app available?',
      answer: 'Currently, Anime HOT or NOT is available as a web application that works on all devices including smartphones and tablets. Our website is fully responsive and optimized for mobile browsing, providing a seamless experience across all screen sizes. We are considering developing dedicated mobile apps in the future based on community feedback.'
    },
    {
      question: 'How can I contact support?',
      answer: 'If you need assistance or have questions that are not answered in this FAQ, you can reach our support team through the contact information provided in the footer of our website. We strive to respond to all inquiries within 24-48 hours during business days.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="Frequently Asked Questions - Anime HOT or NOT Help Center"
        description="Find answers to common questions about using Anime HOT or NOT. Learn how to rate characters, customize your profile, browse anime series, and more."
        keywords="anime hot or not faq, help, how to rate anime characters, anime rating guide, character rating help, anime community questions"
        canonical={`${siteUrl}/faq`}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-4 text-center">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600 mb-12 text-center">
            Find answers to common questions about using Anime HOT or NOT
          </p>

          <div className="space-y-4 mb-12">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h2 className="text-lg font-bold text-gray-900 pr-4">
                    {faq.question}
                  </h2>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-pink-500 transition-transform`}></i>
                  </div>
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-base text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl shadow-xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-lg mb-6 opacity-90">
              Learn more about our platform and community on our About page.
            </p>
            <Link
              to="/about"
              className="inline-block px-8 py-3 text-lg font-semibold text-pink-600 bg-white rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
            >
              About Us
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
